import React, { useEffect, useRef } from 'react'

import { useHistory } from 'router'
import { track } from 'analytics'
import logger from 'logger'
import { useUser } from 'modules/user'

import s from './StorylyComponent.scss'


declare global {
  interface HTMLElementTagNameMap {
    'storyly-web': StorylyElement
  }
}

type StorylyElement = HTMLElement & {
  init: (options: any) => void
  close: () => void
  remove: () => void
}


type InstanceName = 'Home' | 'Main' | 'ScentProfileLead' | 'ScentProfileSubscribed'

const instanceNameToTokenMap: Record<InstanceName, string> = {
  Home: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhY2NfaWQiOjM0ODcsImFwcF9pZCI6MTM0ODYsImluc19pZCI6MTQ2ODR9.Nh9R_39oo-sIBQMm1T4wm3ONNnnjSBhQSdUKnfpMvLU',
  Main: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhY2NfaWQiOjM0ODcsImFwcF9pZCI6MTM0ODYsImluc19pZCI6MTQ3NTZ9.HtWrPXR2p2vGYT058F7eQEJvwgPV1y1zKQQNk-Yh4y0',
  ScentProfileLead: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhY2NfaWQiOjM0ODcsImFwcF9pZCI6MTM0ODYsImluc19pZCI6MTQ3Nzd9.ebz0UCcT2EaIAq6ZSojElsxAwycKc8Hx41pZwRuJxQ8',
  ScentProfileSubscribed: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhY2NfaWQiOjM0ODcsImFwcF9pZCI6MTM0ODYsImluc19pZCI6MTQ5Mjd9.3LGGUrW98lek-o6euPzW9BOHOAuaWB-mcLTGyze8DMA',
}

export type StorylyComponentProps = {
  instance: InstanceName
}

const StorylyComponent: React.FC<StorylyComponentProps> = ({ instance }) => {
  const history = useHistory()
  const { user, isFetching } = useUser()
  const ref = useRef(null)

  useEffect(() => {
    if (isFetching) {
      return
    }

    const storyly = document.createElement('storyly-web')

    const initStoryly = () => {
      // component was unmounted before initialisation
      if (!ref.current) {
        return
      }

      ref.current.appendChild(storyly)

      const segments = []
      if (user) {
        segments.push(user.gender.toLowerCase())

        const subscriptionStatus = user.analyticsMetadata?.subscriptionStatus || 'lead'
        segments.push(subscriptionStatus.toLowerCase())
      }

      if (typeof storyly.init !== 'function') {
        logger.warn('Storyly init failed')
        return
      }

      storyly.init({
        token: instanceNameToTokenMap[instance],
        layout: 'modern',
        segments,
        customStoryGroupHTML: `
            <div class="custom-story-group-container">
              <div class="custom-story-group">
                <img class="storyly__story-group-cover" />
                <span class="storyly__story-group-title" />
              </div>
            </div>
        `,
        events: {
          openStoryGroup: (group) => {
            track('Story icon click', {
              content: group.title,
              position: group.currentOrder + 1,
            })
          },
          onStoryViewed: (story) => {
            track('Story display', {
              content: story.group.title,
              positionInGroup: story.index + 1,
            })
          },
          actionClicked: (story) => {
            const link: string = story?.media?.actionUrl

            if (!link) {
              return
            }

            track('Story click', {
              link,
              positionInGroup: story.index + 1,
            })

            if (link.startsWith('/')) {
              history.push(link)
            }
            else {
              window.open(link, '_blank')
            }
          },
        },
      })
    }

    // Load web component template script only if the Storyly web component is not defined already
    if (!customElements.get('storyly-web')) {
      const script = document.createElement('script')

      script.src = 'https://web-story.storyly.io/v2/storyly-web.js'
      script.setAttribute('type', 'text/javascript')
      script.setAttribute('custom-element', 'storyly-web')

      script.async = true

      ref.current.appendChild(script)

      // We need to init Storyly only after their script is loaded as it contains the web component template
      script.addEventListener('load', () => {
        initStoryly()
      })
    }
    else {
      initStoryly()
    }


    return () => {
      if (typeof storyly.close === 'function') {
        storyly.close()
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ isFetching ])


  return (
    <div className={s.root} ref={ref} />
  )
}

export default StorylyComponent
